import React, { ChangeEvent, useMemo, useState } from 'react';
import { Button, Input, Table } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { Navigate } from 'react-router-dom';
import { ENotificationType } from 'common/const/notification.enum';
import { ERoute } from 'common/const/route.enum';
import { toDataSourceMapper } from 'common/helpers/common.helper';
import { useDebounce } from 'common/hooks/useDebounce';
import { useContentLoader } from 'common/hooks/useContentLoader';
import { ContentLoader } from 'common/components/ContentLoader';
import { ReactComponent as SearchIcon } from 'app/assets/images/search.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { Notification } from 'entities/Modal/components/Notification';
import { CreateAccountModal } from 'entities/Modal/components/CreateAccountModal';
import { IAccount, ICreateAccountPayload } from 'entities/Account/Account.models';
import { filterAccountUsers, renderAccountListRecords } from 'entities/Account/Account.helper';
import { SelectAccountUserModal } from 'entities/Modal/components/SelectAccountUserModal';
import { IUser } from 'entities/User/User.models';
import { EUserStatus } from 'entities/User/User.enum';
import { AddFeedModal } from 'entities/Modal/components/AddFeedModal';
import {
  blockAccount,
  createAccount,
  deleteAccount,
  getAccounts,
  selectAccountError,
  selectAccountList,
  selectIsAccountLoading,
  setAccountError,
  unblockAccount,
  updateAccount,
  updateSellerAccount,
} from 'entities/Account/Account.slice';
import { getUserList, selectIsUserLoading, selectUserList } from 'entities/User/User.slice';
import { loginAsUser, selectXUserId } from 'entities/Auth/Auth.slice';

export const AccountList: React.FC = () => {
  const dispatch = useAppDispatch();
  // auth
  const xUserId = useAppSelector(selectXUserId);
  // account
  const accountList = useAppSelector(selectAccountList);
  const isAccountLoading = useAppSelector(selectIsAccountLoading);
  const accountError = useAppSelector(selectAccountError);
  // user
  const userList = useAppSelector(selectUserList);
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [searchValue, setSearchValue] = useState<string>('');
  const [accountId, setAccountId] = useState<number | null>(null);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [formIsChanged, setFormIsChanged] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [isSeller, setIsSeller] = useState<boolean>(false);
  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const [feedUrl, setFeedUrl] = useState<string>('');
  const [openCreateAccountModal, setOpenCreateAccountModal] = useState<boolean>(false);
  const [openBlockAccountModal, setOpenBlockAccountModal] = useState<boolean>(false);
  const [openDeleteAccountModal, setOpenDeleteAccountModal] = useState<boolean>(false);
  const [openSelectAccountUserModal, setOpenSelectAccountUserModal] = useState<boolean>(false);
  const [openMakeSellerAccountModal, setOpenMakeSellerAccountModal] = useState<boolean>(false);
  const [openAddFeedModal, setOpenAddFeedModal] = useState<boolean>(false);
  const [form] = useForm();

  const selectedAccount = accountList.find((account) => account.id === accountId);
  const dataSource = toDataSourceMapper<IAccount>(accountList);
  const columns = renderAccountListRecords(
    handleLoginToAccountClick,
    handleEditClick,
    handleBlockClick,
    handleUnblockClick,
    handleDeleteClick,
    handleMakeSellerClick,
  );
  const users = useMemo(() => {
    return filterAccountUsers(searchUserValue, userList);
  }, [userList, searchUserValue]);

  const onSearchValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormIsChanged(true);
    setSearchValue(e.target.value);
  };

  const onCreateAccountClick = () => {
    setOpenCreateAccountModal(true);
  };

  const onCreateAccountModalCancelClick = () => {
    form.resetFields();
    dispatch(setAccountError(null));
    setOpenCreateAccountModal(false);

    if (isEdit) {
      setAccountId(null);
      setIsEdit(false);
    }
  };

  async function handleLoginToAccountClick(id: number, isSeller: boolean) {
    const response = await dispatch(getUserList({ accountId: id, status: EUserStatus.Active, limit: 0 }));

    if (response) {
      setIsSeller(isSeller);
      setOpenSelectAccountUserModal(true);
    }
  }

  const onSelectAccountUserModalCancelClick = () => {
    setSelectedUser(null);
    setIsSeller(false);
    setSearchUserValue('');
    setOpenSelectAccountUserModal(false);
  };

  const onSelectAccountUserModalLoginClick = () => {
    if (selectedUser) {
      dispatch(loginAsUser({ user: selectedUser, isSeller }));
      setSelectedUser(null);
    }
  };

  function handleEditClick(record: IAccount) {
    form.setFieldsValue({
      name: record.name,
      email: record.email,
      firstName: record.firstName,
      lastName: record.lastName,
    });

    setAccountId(record.id);
    setIsEdit(true);
    setOpenCreateAccountModal(true);
  }

  function handleBlockClick(id: number) {
    setAccountId(id);
    setOpenBlockAccountModal(true);
  }

  const onBlockAccountModalCancelClick = () => {
    setAccountId(null);
    setOpenBlockAccountModal(false);
  };

  const onBlockAccountModalConfirmClick = () => {
    if (!accountId) {
      return;
    }

    dispatch(
      blockAccount({
        id: accountId,
        onSuccess: () => {
          setAccountId(null);
          setOpenBlockAccountModal(false);
        },
      }),
    );
  };

  function handleUnblockClick(id: number) {
    dispatch(unblockAccount({ id }));
  }

  function handleDeleteClick(id: number) {
    setAccountId(id);
    setOpenDeleteAccountModal(true);
  }

  const onDeleteAccountModalCancelClick = () => {
    setAccountId(null);
    setOpenDeleteAccountModal(false);
  };

  const onDeleteAccountModalConfirmClick = () => {
    if (!accountId) {
      return;
    }

    dispatch(
      deleteAccount({
        id: accountId,
        onSuccess: () => {
          setAccountId(null);
          setOpenDeleteAccountModal(false);
        },
      }),
    );
  };

  function handleMakeSellerClick(id: number) {
    setAccountId(id);
    setOpenMakeSellerAccountModal(true);
  }

  const onMakeSellerAccountModalCancelClick = () => {
    dispatch(setAccountError(null));
    setAccountId(null);
    setOpenMakeSellerAccountModal(false);
  };

  const onMakeSellerAccountModalConfirmClick = () => {
    dispatch(setAccountError(null));

    if (!accountId) {
      return;
    }

    dispatch(
      updateSellerAccount({
        id: accountId,
        onSuccess: () => {
          setOpenMakeSellerAccountModal(false);
          setOpenAddFeedModal(true);
        },
      }),
    );
  };

  const onAddFeedModalCancelClick = () => {
    dispatch(setAccountError(null));
    setAccountId(null);
    setFeedUrl('');
    setOpenAddFeedModal(false);
  };

  const onAddFeedModalConfirmClick = () => {
    dispatch(setAccountError(null));

    if (!accountId) {
      return;
    }

    dispatch(
      updateSellerAccount({
        id: accountId,
        feedUrl,
        onSuccess: onAddFeedModalCancelClick,
      }),
    );
  };

  const onSubmit = (values: ICreateAccountPayload) => {
    dispatch(setAccountError(null));

    if (isEdit) {
      dispatch(
        updateAccount({
          id: accountId as number,
          name: values.name,
          onSuccess: () => {
            form.resetFields();
            setAccountId(null);
            setIsEdit(false);
            setOpenCreateAccountModal(false);
          },
        }),
      );
    } else {
      dispatch(
        createAccount({
          ...values,
          onSuccess: () => {
            form.resetFields();
            setOpenCreateAccountModal(false);
          },
        }),
      );
    }
  };

  useDebounce(() => {
    if (formIsChanged) {
      dispatch(getAccounts({ name: searchValue }));
    }
  }, [searchValue, formIsChanged]);

  const { contentLoading } = useContentLoader(async () => {
    await dispatch(getAccounts({ limit: 0 }));
  });

  if (xUserId) {
    return <Navigate to={ERoute.Profile} />;
  }

  return (
    <div className="account-list">
      {contentLoading ? (
        <ContentLoader />
      ) : (
        <>
          <div className="account-list__header">
            <Input
              className="account-list__search"
              prefix={<SearchIcon />}
              placeholder="Найти аккаунт"
              onChange={onSearchValueChange}
            />

            <Button className="btn btn-primary account-list__create-btn" onClick={onCreateAccountClick}>
              Создать аккаунт
            </Button>
          </div>

          <Table
            dataSource={dataSource}
            columns={columns}
            showHeader={false}
            pagination={false}
            loading={isAccountLoading || isUserLoading}
          />
        </>
      )}

      <CreateAccountModal
        open={openCreateAccountModal}
        form={form}
        loading={isAccountLoading}
        error={accountError}
        isEdit={isEdit}
        onSubmit={onSubmit}
        onCancelClick={onCreateAccountModalCancelClick}
      />

      <Notification
        open={openBlockAccountModal}
        type={ENotificationType.Warning}
        description="Аккаунт клиента будет заблокирован. При блокировке все пользователи, привязанные к Аккаунту клиента, потеряют доступ к данным в системе."
        loading={isAccountLoading}
        onConfirmClick={onBlockAccountModalConfirmClick}
        onCancelClick={onBlockAccountModalCancelClick}
      />

      <Notification
        open={openDeleteAccountModal}
        type={ENotificationType.Warning}
        description="Аккаунт клиента будет удалён. Все данные будут потеряны. Вы уверены, что хотите удалить Аккаунт?"
        loading={isAccountLoading}
        onConfirmClick={onDeleteAccountModalConfirmClick}
        onCancelClick={onDeleteAccountModalCancelClick}
      />

      <SelectAccountUserModal
        open={openSelectAccountUserModal}
        searchValue={searchUserValue}
        users={users}
        selectedUser={selectedUser}
        onSearch={setSearchUserValue}
        onSelect={setSelectedUser}
        onCancel={onSelectAccountUserModalCancelClick}
        onLogin={onSelectAccountUserModalLoginClick}
      />

      <Notification
        open={openMakeSellerAccountModal}
        type={ENotificationType.Notification}
        header={`Сделать аккаунт ${selectedAccount?.name || ''} продавцом`}
        description="Это действие необратимо, вы уверены?"
        loading={isAccountLoading}
        onConfirmClick={onMakeSellerAccountModalConfirmClick}
        onCancelClick={onMakeSellerAccountModalCancelClick}
      />

      <AddFeedModal
        open={openAddFeedModal}
        loading={isAccountLoading}
        error={accountError}
        feedUrl={feedUrl}
        changeFeedUrl={setFeedUrl}
        onCancelClick={onAddFeedModalCancelClick}
        onConfirmClick={onAddFeedModalConfirmClick}
      />
    </div>
  );
};
